import { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isMobile } from 'react-device-detect';
import {
  AiOutlinePrinter,
  AiOutlineSend,
  AiOutlineWhatsApp,
  AiOutlineDollarCircle,
} from 'react-icons/ai';
import { BsFillTrashFill } from 'react-icons/bs';
import { useReactToPrint } from 'react-to-print';
import { Spinner } from 'reactstrap';
import Swal from 'sweetalert2';
import { DefaultButton } from '../../../components/Button/Button';
import { LogoMissingNotice } from '../../../components/LogoMissingNotice/LogoMissingNotice';
import { OsPrint } from '../../../components/OsPrint/OSPrint';
import { OsTermReport } from '../../../components/OSTermReport/OSTermReport';
import { useLogoPreloader } from '../../../core/hooks/useLogoPreloader';
import { saveBudget } from '../../../core/utils/budget';
import { waitForFontsReady } from '../../../core/utils/waitForFontsReady';
import {
  loadFinancialEntriesByOS,
  deleteFinancialEntry,
} from '../../../core/utils/financialEntry';
import { softDeleteBreakdownByEntry } from '../../../core/utils/breakdown';
import {
  loadInstallmentsByEntries,
  deleteInstallment,
} from '../../../core/utils/installment';
import {
  INSTALLMENT_STATUS,
  OS_CATEGORY_ACCOUNT_CODES,
} from '../../../core/constants/financial';
import ChargeRateioModal from './ChargeRateioModal';
import { normalizeDecimalSeparator } from '../../../screens/ModalBudget/utils/functions';
import { alertError, alertSuccess } from '../../../components/Toast';
import { formatCurrency } from '../../../core/utils/format';
import './styles.scss';

export const ActionsButtonOS = (props) => {
  const {
    sendWhatsapp,
    toShare,
    isLoading,
    values,
    onRemove,
    compareValues,
    onSave,
    reloadPrint,
    printCompany,
    client,
    totalSum,
    totalBodyshopService,
    totalParts,
    totalAeService,
    bodyshopService,
    aeService,
    parts,
    observation,
    consultantState,
    budget,
    localSignatureData,
  } = props;

  const user = useSelector((state) => state.app.user || {});
  const company = useSelector((state) => state.app.company || {});
  const [printWithValues, setPrintWithValues] = useState(true);
  const [termReportHeight, setTermReportHeight] = useState(1300);
  const [chargeModalOpen, setChargeModalOpen] = useState(false);
  const [chargePrefilled, setChargePrefilled] = useState(null);
  const printOSRef = useRef();
  const printTermReportRef = useRef();
  const termReportContainerRef = useRef();
  const timeoutsRef = useRef([]);
  const originalTitle = document.title;
  const { logoBase64, waitForReady: waitForLogoReady } = useLogoPreloader(
    printCompany?.logo
  );

  // Monta o rateio líquido por categoria (RN01/RN04): bruto da categoria menos
  // seu desconto, descartando categorias zeradas. Cada categoria carrega o
  // código do plano de contas correspondente (RN02).
  const parseDiscount = (raw) =>
    parseFloat(normalizeDecimalSeparator(raw)) || 0;

  const buildBreakdown = () => {
    const rows = [
      {
        key: 'bodyshop',
        gross: Number(totalBodyshopService) || 0,
        discount: parseDiscount(values?.bodyshop_discount),
      },
      {
        key: 'aesthetics',
        gross: Number(totalAeService) || 0,
        discount: parseDiscount(values?.aesthetics_discount),
      },
      {
        key: 'parts',
        gross: Number(totalParts) || 0,
        discount: parseDiscount(values?.parts_discount),
      },
    ];
    return rows
      .map((r) => ({
        ...r,
        code: OS_CATEGORY_ACCOUNT_CODES[r.key],
        value: Math.max(0, r.gross - r.discount),
      }))
      .filter((r) => r.value > 0);
  };

  const openChargeModalWithOS = () => {
    const clientName = client?.name || values?.client_name || '';
    const vehicleName = values?.vehicle_name || '';
    const docCode = values?.code ? `OS-${values.code}` : `OS-${values.id}`;
    const description = vehicleName
      ? `Cobrança ${docCode} — ${vehicleName}`
      : `Cobrança ${docCode}`;

    setChargePrefilled({
      document: docCode,
      supplier_client: clientName,
      description,
      order_service_id: values.id,
      breakdown: buildBreakdown(),
    });
    setChargeModalOpen(true);
  };

  // Rótulo de um lançamento da OS pro Swal. Cobrança única (modelo novo) mostra o
  // documento — a conta é só a categoria primária, não representa o total. Legado
  // por categoria mostra a conta.
  const entryLabel = (entry) => {
    if (entry.order_service_id) return entry.document || 'Cobrança';
    if (entry.AccountPlan) return `${entry.AccountPlan.code} - ${entry.AccountPlan.name}`;
    return entry.document || '—';
  };

  const describeEntry = (entry) =>
    `<li>${entryLabel(entry)}: <strong>${formatCurrency(entry.total_value)}</strong></li>`;

  const handleChargeClient = async () => {
    if (!values?.id) {
      alertError('Salve a OS antes de gerar a cobrança.');
      return;
    }

    // Idempotência plural — a OS pode ter VÁRIOS lançamentos (um por categoria).
    const existing = await loadFinancialEntriesByOS(values.id);

    if (existing.length === 0) {
      // Caminho normal — primeira cobrança da OS
      if (buildBreakdown().length === 0) {
        alertError('Esta OS não possui valores para cobrar.');
        return;
      }
      openChargeModalWithOS();
      return;
    }

    // Já tem cobrança. Carrega as parcelas de TODOS os lançamentos pra decidir
    // (caminho conservador: bloqueia se ALGUMA parcela de QUALQUER lançamento
    // está paga).
    const entryIds = existing.map((e) => e.id);
    const installments = await loadInstallmentsByEntries(entryIds);
    const paidCount = (installments || []).filter(
      (i) => i.status === INSTALLMENT_STATUS.PAGO
    ).length;
    const totalCount = installments?.length || 0;
    const aggregateValue = existing.reduce(
      (s, e) => s + (Number(e.total_value) || 0),
      0
    );
    const entriesHtml = existing.map(describeEntry).join('');

    if (paidCount > 0) {
      // BLOQUEIO — não permite cancelar quando há recebimentos
      Swal.fire({
        title: 'Cobrança já recebida',
        html: `
          <p>Esta OS tem cobrança vinculada (total ${formatCurrency(aggregateValue)}) com <strong>${paidCount} de ${totalCount}</strong> parcela(s) já recebida(s) no banco.</p>
          <ul style="text-align: left; margin: 12px auto; max-width: 360px; font-size: 14px;">${entriesHtml}</ul>
          <p style="margin-top: 12px; color: #777; font-size: 14px;">Não é possível gerar uma nova cobrança para esta OS.</p>
        `,
        icon: 'info',
        confirmButtonText: 'Entendi',
      });
      return;
    }

    // Todas parcelas pendentes — permite cancelar e gerar nova
    const result = await Swal.fire({
      title: 'Cobrança já gerada',
      html: `
        <p>Esta OS já tem cobrança vinculada (total ${formatCurrency(aggregateValue)}) com <strong>${totalCount}</strong> parcela(s) pendente(s):</p>
        <ul style="text-align: left; margin: 12px auto; max-width: 360px; font-size: 14px;">${entriesHtml}</ul>
        <p style="margin-top: 12px;">Como nenhuma foi recebida ainda, você pode cancelar a cobrança atual e criar uma nova.</p>
      `,
      icon: 'info',
      showCancelButton: true,
      showConfirmButton: false,
      showDenyButton: true,
      denyButtonText: '🗑 Cancelar cobrança e gerar nova',
      cancelButtonText: 'Fechar',
      reverseButtons: true,
    });

    if (!result.isDenied) return;

    // 2ª confirmação
    const confirm = await Swal.fire({
      title: 'Tem certeza?',
      text: 'A cobrança atual e suas parcelas serão removidas. Esta ação não pode ser desfeita.',
      icon: 'warning',
      showCancelButton: true,
      showConfirmButton: false,
      showDenyButton: true,
      denyButtonText: 'Sim, cancelar e refazer',
      cancelButtonText: 'Voltar',
      reverseButtons: true,
    });

    if (!confirm.isDenied) return;

    // Soft-delete das parcelas (todas pendentes — sem reversão de saldo) e de
    // todos os lançamentos vinculados à OS.
    try {
      for (const inst of installments) {
        await deleteInstallment(inst.id, user.id);
      }
      for (const entry of existing) {
        await softDeleteBreakdownByEntry(entry.id, user.id);
        await deleteFinancialEntry(entry.id, user.id);
      }
      alertSuccess('Cobrança anterior cancelada. Crie a nova cobrança.');
      openChargeModalWithOS();
    } catch (err) {
      alertError('Erro ao cancelar cobrança anterior.');
    }
  };

  // Cleanup de timeouts ao desmontar componente
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      timeoutsRef.current = [];
    };
  }, []);

  const handleShare = () => {
    // No mobile, usar radio buttons para permitir apenas uma seleção
    const inputType = isMobile ? 'radio' : 'checkbox';
    const inputName = isMobile ? 'shareDoc' : '';

    Swal.fire({
      title: 'Opções de Compartilhamento',
      html: `
        <div style="text-align: left; margin: 20px 0;">
          ${isMobile ? '<p style="color: #666; font-size: 14px; margin-bottom: 15px;">No mobile, você pode compartilhar apenas um documento por vez.</p>' : ''}
          <div style="margin-bottom: 20px;">
            <label style="display: block; margin-bottom: 10px; font-weight: bold;">Documentos:</label>
            <div style="margin-left: 10px;">
              <label style="display: flex; align-items: center; margin-bottom: 8px; cursor: pointer;">
                <input type="${inputType}" ${inputName ? `name="${inputName}"` : ''} id="shareOS" value="os" ${!isMobile ? 'checked' : ''} style="margin-right: 8px; width: 18px; height: 18px; cursor: pointer;" />
                <span>Ordem de Serviço</span>
              </label>
              <label style="display: flex; align-items: center; margin-bottom: 8px; cursor: pointer;">
                <input type="${inputType}" ${inputName ? `name="${inputName}"` : ''} id="shareLaudo" value="laudo" ${isMobile ? 'checked' : ''} style="margin-right: 8px; width: 18px; height: 18px; cursor: pointer;" />
                <span>Laudo/Termo</span>
              </label>
            </div>
          </div>
          <div id="shareOsValueOptions" style="margin-bottom: 20px;">
            <label style="display: block; margin-bottom: 10px; font-weight: bold;">Ordem de Serviço:</label>
            <div style="margin-left: 10px;">
              <label style="display: flex; align-items: center; margin-bottom: 8px; cursor: pointer;">
                <input type="radio" name="shareOsValues" value="with" checked style="margin-right: 8px; width: 18px; height: 18px; cursor: pointer;" />
                <span>Com valores</span>
              </label>
              <label style="display: flex; align-items: center; margin-bottom: 8px; cursor: pointer;">
                <input type="radio" name="shareOsValues" value="without" style="margin-right: 8px; width: 18px; height: 18px; cursor: pointer;" />
                <span>Sem valores</span>
              </label>
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Compartilhar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        let shareOS, shareLaudo;

        if (isMobile) {
          // No mobile, pegar o valor do radio button selecionado
          const selectedDoc = document.querySelector('input[name="shareDoc"]:checked')?.value;
          shareOS = selectedDoc === 'os';
          shareLaudo = selectedDoc === 'laudo';
        } else {
          // No desktop, pegar os checkboxes
          shareOS = document.getElementById('shareOS').checked;
          shareLaudo = document.getElementById('shareLaudo').checked;
        }

        const osWithValues =
          document.querySelector('input[name="shareOsValues"]:checked')
            .value === 'with';

        if (!shareOS && !shareLaudo) {
          Swal.showValidationMessage(
            'Selecione pelo menos um documento para compartilhar'
          );
          return false;
        }

        return { shareOS, shareLaudo, osWithValues };
      },
      didOpen: () => {
        const shareOSCheckbox = document.getElementById('shareOS');
        const shareLaudoCheckbox = document.getElementById('shareLaudo');
        const shareOsValueOptions = document.getElementById(
          'shareOsValueOptions'
        );

        // Função para atualizar visibilidade das opções de valores
        const updateValueOptions = () => {
          const osSelected = isMobile
            ? document.querySelector('input[name="shareDoc"]:checked')?.value === 'os'
            : shareOSCheckbox.checked;

          shareOsValueOptions.style.opacity = osSelected ? '1' : '0.4';
          shareOsValueOptions.style.pointerEvents = osSelected ? 'auto' : 'none';
        };

        // Adicionar event listeners
        if (isMobile) {
          shareOSCheckbox.addEventListener('change', updateValueOptions);
          shareLaudoCheckbox.addEventListener('change', updateValueOptions);
        } else {
          shareOSCheckbox.addEventListener('change', updateValueOptions);
        }

        // Inicializar estado
        updateValueOptions();

        // Store listeners for cleanup
        window._swalListeners = { shareOSCheckbox, shareLaudoCheckbox, updateValueOptions };
      },
      willClose: () => {
        // Cleanup: remove event listeners to prevent memory leak
        if (window._swalListeners) {
          const { shareOSCheckbox, shareLaudoCheckbox, updateValueOptions } = window._swalListeners;
          if (shareOSCheckbox) shareOSCheckbox.removeEventListener('change', updateValueOptions);
          if (shareLaudoCheckbox) shareLaudoCheckbox.removeEventListener('change', updateValueOptions);
          delete window._swalListeners;
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const { shareOS, shareLaudo, osWithValues } = result.value;
        setPrintWithValues(osWithValues);

        // Chamar imediatamente sem setTimeout para manter o contexto do usuário
        toShare({
          shareOS,
          shareLaudo,
          printOSRef,
          printTermReportRef,
        });
      }
    });
  };

  useLayoutEffect(() => {
    if (termReportContainerRef.current) {
      const height = termReportContainerRef.current.offsetHeight;
      setTermReportHeight(height + 50);
    }
  }, [values, reloadPrint]);

  const toPrint = () => {
    // No mobile, usar radio buttons para permitir apenas uma seleção
    const inputType = isMobile ? 'radio' : 'checkbox';
    const inputName = isMobile ? 'printDoc' : '';

    Swal.fire({
      title: 'Opções de Impressão',
      html: `
        <div style="text-align: left; margin: 20px 0;">
          ${isMobile ? '<p style="color: #666; font-size: 14px; margin-bottom: 15px;">No mobile, você pode imprimir apenas um documento por vez.</p>' : ''}
          <div style="margin-bottom: 20px;">
            <label style="display: block; margin-bottom: 10px; font-weight: bold;">Documentos:</label>
            <div style="margin-left: 10px;">
              <label style="display: flex; align-items: center; margin-bottom: 8px; cursor: pointer;">
                <input type="${inputType}" ${inputName ? `name="${inputName}"` : ''} id="printOS" value="os" ${!isMobile ? 'checked' : ''} style="margin-right: 8px; width: 18px; height: 18px; cursor: pointer;" />
                <span>Ordem de Serviço</span>
              </label>
              <label style="display: flex; align-items: center; margin-bottom: 8px; cursor: pointer;">
                <input type="${inputType}" ${inputName ? `name="${inputName}"` : ''} id="printLaudo" value="laudo" ${isMobile ? 'checked' : ''} style="margin-right: 8px; width: 18px; height: 18px; cursor: pointer;" />
                <span>Laudo/Termo</span>
              </label>
            </div>
          </div>
          <div id="osValueOptions" style="margin-bottom: 20px;">
            <label style="display: block; margin-bottom: 10px; font-weight: bold;">Ordem de Serviço:</label>
            <div style="margin-left: 10px;">
              <label style="display: flex; align-items: center; margin-bottom: 8px; cursor: pointer;">
                <input type="radio" name="osValues" value="with" checked style="margin-right: 8px; width: 18px; height: 18px; cursor: pointer;" />
                <span>Com valores</span>
              </label>
              <label style="display: flex; align-items: center; margin-bottom: 8px; cursor: pointer;">
                <input type="radio" name="osValues" value="without" style="margin-right: 8px; width: 18px; height: 18px; cursor: pointer;" />
                <span>Sem valores</span>
              </label>
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Imprimir',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        let printOS, printLaudo;

        if (isMobile) {
          // No mobile, pegar o valor do radio button selecionado
          const selectedDoc = document.querySelector('input[name="printDoc"]:checked')?.value;
          printOS = selectedDoc === 'os';
          printLaudo = selectedDoc === 'laudo';
        } else {
          // No desktop, pegar os checkboxes
          printOS = document.getElementById('printOS').checked;
          printLaudo = document.getElementById('printLaudo').checked;
        }

        const osWithValues =
          document.querySelector('input[name="osValues"]:checked').value ===
          'with';

        if (!printOS && !printLaudo) {
          Swal.showValidationMessage(
            'Selecione pelo menos um documento para imprimir'
          );
          return false;
        }

        return { printOS, printLaudo, osWithValues };
      },
      didOpen: () => {
        const printOSCheckbox = document.getElementById('printOS');
        const printLaudoCheckbox = document.getElementById('printLaudo');
        const osValueOptions = document.getElementById('osValueOptions');

        // Função para atualizar visibilidade das opções de valores
        const updateValueOptions = () => {
          const osSelected = isMobile
            ? document.querySelector('input[name="printDoc"]:checked')?.value === 'os'
            : printOSCheckbox.checked;

          osValueOptions.style.opacity = osSelected ? '1' : '0.4';
          osValueOptions.style.pointerEvents = osSelected ? 'auto' : 'none';
        };

        // Adicionar event listeners
        if (isMobile) {
          printOSCheckbox.addEventListener('change', updateValueOptions);
          printLaudoCheckbox.addEventListener('change', updateValueOptions);
        } else {
          printOSCheckbox.addEventListener('change', updateValueOptions);
        }

        // Inicializar estado
        updateValueOptions();

        // Store listeners for cleanup
        window._swalPrintListeners = { printOSCheckbox, printLaudoCheckbox, updateValueOptions };
      },
      willClose: () => {
        // Cleanup: remove event listeners to prevent memory leak
        if (window._swalPrintListeners) {
          const { printOSCheckbox, printLaudoCheckbox, updateValueOptions } = window._swalPrintListeners;
          if (printOSCheckbox) printOSCheckbox.removeEventListener('change', updateValueOptions);
          if (printLaudoCheckbox) printLaudoCheckbox.removeEventListener('change', updateValueOptions);
          delete window._swalPrintListeners;
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const { printOS, printLaudo, osWithValues } = result.value;

        const executePrints = () => {
          if (printOS && printLaudo) {
            setPrintWithValues(osWithValues);
            const timeout1 = setTimeout(() => {
              handlePrintOS();
              const timeout2 = setTimeout(() => {
                handlePrintLaudo();
              }, 1000);
              timeoutsRef.current.push(timeout2);
            }, 500);
            timeoutsRef.current.push(timeout1);
          } else if (printOS) {
            setPrintWithValues(osWithValues);
            const timeout = setTimeout(() => {
              handlePrintOS();
            }, 500);
            timeoutsRef.current.push(timeout);
          } else if (printLaudo) {
            const timeout = setTimeout(() => {
              handlePrintLaudo();
            }, 500);
            timeoutsRef.current.push(timeout);
          }
        };

        if (!compareValues) {
          executePrints();
        } else {
          Swal.fire({
            title: 'Salve o conteúdo para poder imprimir o PDF.',
            showCancelButton: true,
            showConfirmButton: true,
            showDenyButton: false,
            icon: 'warning',
            confirmButtonText: `Salvar`,
            cancelButtonText: 'Cancelar',
          }).then((saveResult) => {
            if (saveResult.isConfirmed) {
              onSave(executePrints);
            }
          });
        }
      }
    });
  };

  const handlePrintOS = useReactToPrint({
    content: () => printOSRef.current,

    onBeforeGetContent: async () => {
      const newTitle = `Orcamento_${values.code}_${values.client_name}_${values.vehicle_name}}`;
      document.title = newTitle;
      // Sem logo+fontes prontos, o print do iframe sai com logo gigante ou fonte trocada.
      await Promise.all([waitForLogoReady(4000), waitForFontsReady(3000)]);
    },

    onAfterPrint: () => {
      document.title = originalTitle;
    },

    onBeforePrint: async () => {
      const budgetId = values.id;
      if (values?.shared_at === null) {
        await saveBudget(budgetId, {
          shared_at: new Date(),
        });
      }
    },
  });

  const handlePrintLaudo = useReactToPrint({
    content: () => printTermReportRef.current,

    onBeforeGetContent: async () => {
      const newTitle = `Laudo_${values.code}_${values.client_name}_${values.vehicle_name}}`;
      document.title = newTitle;
      // Sem logo+fontes prontos, o print do iframe sai com logo gigante ou fonte trocada.
      await Promise.all([waitForLogoReady(4000), waitForFontsReady(3000)]);
    },

    onAfterPrint: () => {
      document.title = originalTitle;
    },

    onBeforePrint: async () => {
      const budgetId = values.id;
      if (values?.shared_at === null) {
        await saveBudget(budgetId, {
          shared_at: new Date(),
        });
      }
    },
  });

  return (
    <>
      <div style={{ marginBottom: 16, position: 'relative', zIndex: 10 }}>
        {values?.id && !printCompany?.logo && (
          <LogoMissingNotice companyId={printCompany?.id} />
        )}
        {values?.id && (
          <div
            className="header-title budget-action-button-container"
            style={{
              gap: isMobile ? 8 : 16,
              marginTop: 8,
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: isMobile ? 'space-between' : 'flex-start',
              alignItems: 'center',
            }}
          >
            {isMobile ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  width: '100%',
                }}
              >
                <DefaultButton
                  className="modal-print modal-footer-button cancel margin-0"
                  label={'IMPRIMIR'}
                  icon={<AiOutlinePrinter className="button-icon-cancel" />}
                  onClick={toPrint}
                  style={{ width: '100%' }}
                />
                <DefaultButton
                  className="modal-footer-button cancel margin-0"
                  label={'ENVIAR'}
                  icon={<AiOutlineWhatsApp className="button-icon-cancel" />}
                  onClick={sendWhatsapp}
                  style={{ width: '100%' }}
                />
                <DefaultButton
                  className="modal-footer-button cancel margin-0"
                  label={'COMPARTILHAR'}
                  icon={<AiOutlineSend className="button-icon-cancel" />}
                  onClick={handleShare}
                  disabled={isLoading}
                  style={{ width: '100%' }}
                />
                <DefaultButton
                  className="modal-footer-button cancel margin-0"
                  label={'COBRAR CLIENTE'}
                  icon={<AiOutlineDollarCircle className="button-icon-cancel" />}
                  onClick={handleChargeClient}
                  style={{ width: '100%' }}
                />
                <DefaultButton
                  className="modal-footer-button delete"
                  label={'Excluir'}
                  icon={<BsFillTrashFill className="button-icon-cancel" />}
                  onClick={onRemove}
                  style={{ width: '100%' }}
                />
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <DefaultButton
                  className="modal-print modal-footer-button cancel margin-0"
                  label={'IMPRIMIR'}
                  icon={<AiOutlinePrinter className="button-icon-cancel" />}
                  onClick={toPrint}
                  style={{ minWidth: '140px' }}
                />
                <DefaultButton
                  className="modal-footer-button cancel margin-0"
                  label={'ENVIAR'}
                  icon={<AiOutlineWhatsApp className="button-icon-cancel" />}
                  onClick={sendWhatsapp}
                  style={{ minWidth: '110px' }}
                />
                <DefaultButton
                  className="modal-footer-button cancel margin-0"
                  label={'COMPARTILHAR'}
                  icon={<AiOutlineSend className="button-icon-cancel" />}
                  onClick={handleShare}
                  disabled={isLoading}
                  style={{ minWidth: '140px' }}
                />
                <DefaultButton
                  className="modal-footer-button cancel margin-0"
                  label={'COBRAR CLIENTE'}
                  icon={<AiOutlineDollarCircle className="button-icon-cancel" />}
                  onClick={handleChargeClient}
                  style={{ minWidth: '160px' }}
                />
                {isLoading && (
                  <div className="spinner-clear" style={{ marginLeft: '16px' }}>
                    <Spinner color="primary" size="sm" />
                  </div>
                )}
                <DefaultButton
                  className="modal-footer-button delete"
                  label={'Excluir'}
                  icon={<BsFillTrashFill className="button-icon-cancel" />}
                  onClick={onRemove}
                  style={{
                    minWidth: '120px',
                    marginLeft: 'auto',
                  }}
                />
              </div>
            )}
            {isMobile && isLoading && (
              <div className="spinner-clear" style={{ marginTop: '8px' }}>
                <Spinner color="primary" size="sm" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Componentes de impressão - sempre renderizados mas escondidos */}
      <div
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          width: '800px',
          minHeight: '1000px',
        }}
      >
        <div
          ref={termReportContainerRef}
          style={{
            width: '800px',
            backgroundColor: 'white',
          }}
        >
          <OsTermReport
            consultantState={consultantState}
            ref={printTermReportRef}
            reloadPrint={reloadPrint}
            company={printCompany}
            logoBase64={logoBase64}
            client={client}
            values={values}
            isLoading={isLoading}
            localSignatureData={localSignatureData}
          />
        </div>
        <div
          style={{
            marginTop: '50px',
            width: '800px',
            backgroundColor: 'white',
          }}
        >
          <OsPrint
            printWithValues={printWithValues}
            consultantState={consultantState}
            bodyshopService={bodyshopService}
            aeService={aeService}
            parts={parts}
            ref={printOSRef}
            reloadPrint={reloadPrint}
            company={printCompany}
            logoBase64={logoBase64}
            client={client}
            totalSum={totalSum}
            totalBodyshopService={totalBodyshopService}
            totalParts={totalParts}
            values={values}
            isLoading={isLoading}
            totalServices={totalBodyshopService + totalAeService}
            totalAeService={totalAeService}
            observation={observation}
          />
        </div>
      </div>

      {/* Visualização desktop dos PDFs */}
      {!isMobile && (
        <div style={{ marginTop: '50px', position: 'relative', zIndex: 1 }}>
          <div style={{ marginBottom: '50px' }}>
            <OsTermReport
              consultantState={consultantState}
              reloadPrint={reloadPrint}
              company={printCompany}
              logoBase64={logoBase64}
              client={client}
              values={values}
              isLoading={isLoading}
              localSignatureData={localSignatureData}
            />
          </div>
          <div>
            <OsPrint
              printWithValues={printWithValues}
              consultantState={consultantState}
              bodyshopService={bodyshopService}
              aeService={aeService}
              parts={parts}
              reloadPrint={reloadPrint}
              company={printCompany}
              logoBase64={logoBase64}
              client={client}
              totalSum={totalSum}
              totalBodyshopService={totalBodyshopService}
              totalParts={totalParts}
              values={values}
              isLoading={isLoading}
              totalServices={totalBodyshopService + totalAeService}
              totalAeService={totalAeService}
              observation={observation}
            />
          </div>
        </div>
      )}

      {/* Modal de cobrança — abre pelo botão "Cobrar Cliente" com o rateio
          automático por categoria (1.1/1.2/1.3) pré-calculado a partir da OS. */}
      <ChargeRateioModal
        isOpen={chargeModalOpen}
        onClose={() => {
          setChargeModalOpen(false);
          setChargePrefilled(null);
        }}
        onSaved={() => {
          setChargeModalOpen(false);
          setChargePrefilled(null);
        }}
        prefilled={chargePrefilled}
      />
    </>
  );
};
